import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  sendForSignature,
  downloadSignedPdfWithName,
  triggerDownloadFromResponse,
  deleteContract,
  markLinkSent,
  updateContractStatus,
  setContractVpn,
} from '../api/contracts.js';
import { listTerminationsByContract, downloadTerminationPdf, sendTerminationForSignature, deleteTermination } from '../api/terminations.js';
import CreateTerminationModal from './CreateTerminationModal';
import {
  listInvoicesByContract,
  downloadInvoicePdf,
  updateInvoicePeriod,
  updateInvoiceStatus,
  deleteInvoice,                 // Phase 75.1: permanent-delete for ADMIN/ACCOUNTANT
} from '../api/invoices.js';
import { useCurrentUser } from '../hooks/useCurrentUser.js';
import { queryKeys } from '../lib/queryKeys';
import { contractDeleteErrorMessage } from '../lib/contractDeleteError.js';
import { useToast } from '../contexts/ToastContext';
import { copyToClipboard } from '../utils.js';
import AmbassadorDataModal from './AmbassadorDataModal';
import CreateInvoiceModal from './CreateInvoiceModal';
import EditDraftInvoiceModal from './EditDraftInvoiceModal';
import InvoiceStatusLogModal from './InvoiceStatusLogModal';
// Phase 75 D-38 / D-40 + Phase 75.1: invoice status now rendered via the interactive
// InvoiceStatusDropdown (shared with InvoicesView). The variant/label maps live inside
// that component, so this file no longer imports them directly.
import InvoiceStatusDropdown from './InvoiceStatusDropdown.jsx';
import { paymentBasisLabel } from '../lib/paymentBasis';
import { APPS, appBadge } from '../constants/apps.js';
import DocumentActions from './DocumentActions.jsx';
import { Icon } from './ds';
// Phase 101 W3: Pack rebind UI
import { usePacksByLead, useCreatePack, useRebindInvoiceToPack } from '../api/packs.js';
import MonthPicker from './MonthPicker.jsx';
import { CONTRACT_STATUS, isSignedContract } from '../lib/contractStatus';
import { formatPaymentMethod } from '../lib/paymentMethod';
// Phase 68 D-01 / D-04 / D-12: clickable period zone uses MonthPicker + formatters from periodLabel.
import { formatMonthYear, formatCompactDate } from '../lib/periodLabel.js';
import { packDisplayName } from '../lib/packName.js';

// P-D2 D4: status → chip tone. Module-local, NOT a new export — lib/contractStatus.js
// keeps `color` as the source of truth for AmbassadorRow's table pill.
const CONTRACT_TONE = {
  draft: 'neutral', link_sent: 'orange', form_sent: 'blue', submitted: 'purple',
  reviewing: 'orange', sent_to_sign: 'blue', sent_to_docusign: 'blue',
  completed: 'green', terminated: 'green',
};
// P-D2 D6: paymentBasis → chip tone. `direct` is neutral, not orange: it sits next to an
// orange kind='test' chip on 35 dev invoices, and two adjacent oranges read as one thing.
const BASIS_TONE = { ambassador: 'green', ad_hoc: 'purple', direct: 'neutral' };

// Phase 101 W3 — PackRebindPopover (shared with InvoicesView — same logic).
function PackRebindPopover ({ invoice, contractId, leadId, onClose }) {
  const ref = useRef(null);
  const { addToast } = useToast();

  const { data: packs = [], isLoading: packsLoading } = usePacksByLead(leadId);
  const createPack = useCreatePack();
  const rebind = useRebindInvoiceToPack();
  const [selectedPackId, setSelectedPackId] = useState(
    packs.length === 1 ? packs[0].id : (invoice?.packId ?? null),
  );
  // PACK-APP-REQ: the new pack's application (0-packs branch) — required, no default.
  const [newPackApp, setNewPackApp] = useState('');

  useEffect(() => {
    if (packs.length === 1 && !selectedPackId) setSelectedPackId(packs[0].id);
  }, [packs, selectedPackId]);

  useEffect(() => {
    function handler (e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const packLabel = packDisplayName;

  const handleRebind = (packId) => {
    rebind.mutate(
      { invoiceId: invoice.id, packId, contractId },
      {
        onSuccess: () => { addToast('Готово', 'Инвойс привязан к пачке', 'success'); onClose(); },
        onError: (err) => {
          const code = err?.response?.data?.code;
          if (code === 'contract_not_completed') addToast('Ошибка', 'Договор должен быть в статусе COMPLETED', 'error');
          else if (code === 'pack_lead_mismatch') addToast('Ошибка', 'Пачка принадлежит другому блогеру', 'error');
          else addToast('Ошибка', err?.response?.data?.error || 'Ошибка перепривязки', 'error');
        },
      },
    );
  };

  const handleCreateAndRebind = () => {
    createPack.mutate(
      { leadId, application: newPackApp },
      {
        onSuccess: (data) => {
          const newPackId = data?.pack?.id ?? data?.id;
          if (newPackId) handleRebind(newPackId);
          else addToast('Ошибка', 'Пачка создана, но ID не получен', 'error');
        },
        onError: () => addToast('Ошибка', 'Не удалось создать пачку', 'error'),
      },
    );
  };

  const isBusy = rebind.isPending || createPack.isPending;

  return (
    <div ref={ref} className="pack-rebind-popover oc-lead-card__docs-rebind">
      {/* Tokenised, not redesigned (D3). The surviving inline objects carry layout §6.6
          declares no rule for; none is lint-flagged (bare numbers, not hex or px strings). */}
      <div className="oc-lead-card__docs-rebind-head">
        <span>Привязать к пачке</span>
        <button type="button" onClick={onClose} className="oc-lead-card__docs-rebind-close" style={{ fontSize: 16 }}>×</button>
      </div>
      {packsLoading ? (
        <div>Загрузка…</div>
      ) : packs.length === 0 ? (
        <div>
          <div className="legacy-pack-banner">У этого блогера нет пачек. Создать?</div>
          <select className="oc-lead-card__docs-rebind-select" style={{ marginTop: 8 }} aria-label="Приложение"
            value={newPackApp} onChange={(e) => setNewPackApp(e.target.value)}>
            <option value="">— Выберите приложение —</option>
            {APPS.map((a) => <option key={a.value} value={a.value}>{a.value}</option>)}
          </select>
          <button type="button" className="documents-action-btn" style={{ width: '100%', marginTop: 8 }} disabled={isBusy || !newPackApp} onClick={handleCreateAndRebind}>
            {isBusy ? 'Создание…' : 'Создать пачку и привязать'}
          </button>
        </div>
      ) : packs.length === 1 ? (
        <div>
          <div style={{ marginBottom: 8 }}>{packLabel(packs[0])}</div>
          <button type="button" className="documents-action-btn" style={{ width: '100%' }} disabled={isBusy} onClick={() => handleRebind(packs[0].id)}>
            {isBusy ? 'Привязка…' : `Привязать к ${packs[0].label}`}
          </button>
        </div>
      ) : (
        <div>
          <select className="oc-lead-card__docs-rebind-select" style={{ marginBottom: 8 }}
            value={selectedPackId ?? ''} onChange={(e) => setSelectedPackId(e.target.value || null)}>
            <option value="">— Выберите пачку —</option>
            {packs.map((p) => <option key={p.id} value={p.id}>{packLabel(p)}</option>)}
          </select>
          <button type="button" className="documents-action-btn" style={{ width: '100%' }} disabled={isBusy || !selectedPackId} onClick={() => selectedPackId && handleRebind(selectedPackId)}>
            {isBusy ? 'Привязка…' : 'Привязать'}
          </button>
        </div>
      )}
    </div>
  );
}

const formatDate = (val) => {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return val;
  }
};

const buildFormUrl = (token) => `https://appeal.lawdrawing.online/form/${token}`;

export default function ContractCard({ contract, lead }) {
  const qc = useQueryClient();
  const { addToast } = useToast();
  // Phase 75.1: re-introduced for the inline invoice-status dropdown's MANAGER RBAC hint
  // (frontend cosmetic only — backend assertTransition is the authoritative gate).
  // isAdmin / isAccountant / isTeamLead gate the permanent-delete button on void
  // invoices (P4 2026-06-01 added TEAM_LEAD; mirrors backend DELETE_INVOICE_ROLES).
  const { user, isAdmin, isAccountant, isTeamLead } = useCurrentUser();
  const canDeleteVoid = isAdmin || isAccountant || isTeamLead;
  // Phase 2 (2026-06-12): period edit restricted to руководители (ADMIN/TEAM_LEAD) —
  // backend PATCH /invoices/:id/period now 403s everyone else (manager-own revoked).
  const canEditPeriod = isAdmin || isTeamLead;
  const [viewDataOpen, setViewDataOpen] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [terminationModalOpen, setTerminationModalOpen] = useState(false);
  // Phase 65 D-19: invoice status-log modal trigger.
  const [logModalInvoiceId, setLogModalInvoiceId] = useState(null);
  // Phase 68 D-01: track which invoice (if any) is in edit-period mode.
  // Inline-edit, single field at a time; null = none.
  const [editingPeriodInvoiceId, setEditingPeriodInvoiceId] = useState(null);
  // Phase 100: editing account + videoCount on a draft invoice (modal-driven).
  const [editingDraftInvoice, setEditingDraftInvoice] = useState(null);
  // Phase 101 W3: Pack rebind popover per invoice row
  const [rebindFor, setRebindFor] = useState(null);
  // Phase 75 OQ-1: role flags removed alongside permanent-delete button.

  const { data: invoices = [], isLoading: invoicesLoading } = useQuery({
    queryKey: queryKeys.invoices.byContract(contract.id),
    queryFn: () => listInvoicesByContract(contract.id),
    staleTime: 15_000,
  });

  const sortedInvoices = [...invoices].sort((a, b) => (b.seqNo ?? 0) - (a.seqNo ?? 0));

  // INVOICE-FORM-UX E3: the «Инвойсы» list's ✏️ lands here as ?draft=<invoice id>. The card that owns the
  // draft opens its editor (the same two setters as its own ✏️) and removes the parameter, so a reload
  // does not re-open it and the same ✏️ can open it again.
  const [searchParams, setSearchParams] = useSearchParams();
  const [focusId, setFocusId] = useState(null);
  const draftParam = searchParams.get('draft');
  useEffect(() => {
    if (!draftParam) return;
    const inv = invoices.find((i) => i.id === draftParam);
    if (!inv) return;                                  // another contract's draft, or the list is still loading
    if (inv.status === 'draft' && inv.paymentBasis !== 'direct') {
      setInvoiceModalOpen(false);
      setEditingDraftInvoice(inv);
    } else {
      setFocusId(inv.id);
      requestAnimationFrame(() => {
        document.querySelector(`[data-invoice-id="${inv.id}"]`)?.scrollIntoView({ block: 'center' });
      });
    }
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('draft'); return next; }, { replace: true });
  }, [draftParam, invoices]);

  const showTerminations = isSignedContract(contract.status);
  const { data: terminations = [] } = useQuery({
    queryKey: ['terminations', contract.id],
    queryFn: () => listTerminationsByContract(contract.id),
    enabled: showTerminations,
  });

  const invalidateContracts = () => {
    qc.invalidateQueries({ queryKey: queryKeys.contracts.byLead(lead.id) });
    qc.invalidateQueries({ queryKey: queryKeys.contracts.all });
  };

  const sendMutation = useMutation({
    mutationFn: sendForSignature,
    onSuccess: () => {
      invalidateContracts();
      addToast('Успешно', 'Контракт отправлен на подпись', 'success');
    },
    onError: () => addToast('Ошибка', 'Не удалось отправить на подпись', 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteContract,
    onSuccess: () => {
      invalidateContracts();
      addToast('Удалено', 'Договор удалён', 'success');
    },
    // C4/P6b: бэкенд отвечает 409 { error, code:'money_guard' }, когда к договору
    // привязаны инвойсы/расторжения — показываем причину, а не общий отказ.
    onError: (err) => addToast('Ошибка', contractDeleteErrorMessage(err), 'error'),
  });

  // Phase 57-01: manual «Ссылка отправлена» transition (draft → link_sent)
  const linkSentMutation = useMutation({
    mutationFn: () => markLinkSent(contract.id),
    onSuccess: () => {
      invalidateContracts();
      addToast('Отмечено', 'Статус изменён на «Ожидаем данные»', 'success');
    },
    onError: () => addToast('Ошибка', 'Не удалось изменить статус', 'error'),
  });

  const terminateMutation = useMutation({
    mutationFn: () => updateContractStatus(contract.id, 'terminated'),
    onSuccess: () => {
      invalidateContracts();
      addToast('Готово', 'Договор расторгнут', 'success');
    },
    onError: () => addToast('Ошибка', 'Не удалось расторгнуть договор', 'error'),
  });

  // P-E3: the flag is an operational fact — no status guard, no confirm dialog (D6).
  const vpnMutation = useMutation({
    mutationFn: (next) => setContractVpn(contract.id, next),
    onSuccess: (_d, next) => {
      invalidateContracts();
      addToast('Готово', next ? 'Постинг через VPN включён' : 'Постинг через VPN выключен', 'success');
    },
    onError: () => addToast('Ошибка', 'Не удалось изменить флаг VPN', 'error'),
  });

  const sendTerminationMutation = useMutation({
    mutationFn: (id) => sendTerminationForSignature(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['terminations', contract.id] });
      addToast('Готово', 'Отправлено на подпись', 'success');
    },
    onError: () => addToast('Ошибка', 'Не удалось отправить на подпись', 'error'),
  });

  const handleCopyFormLink = async () => {
    if (!contract?.formToken) return;
    await copyToClipboard(buildFormUrl(contract.formToken));
    addToast('Скопировано', 'Ссылка на форму скопирована', 'success');
  };

  const handleDownloadSignedPdf = async () => {
    try {
      const { blob, filename } = await downloadSignedPdfWithName(contract.id);
      triggerDownloadFromResponse(blob, filename, `contract-${contract.id}-signed.pdf`);
    } catch {
      addToast('Ошибка', 'Не удалось скачать PDF', 'error');
    }
  };

  const handleDelete = () => {
    if (window.confirm('Удалить этот договор? Договор с инвойсами или расторжениями удалить нельзя.')) {
      deleteMutation.mutate(contract.id);
    }
  };

  const statusMeta = CONTRACT_STATUS[contract.status] || { label: contract.status, tone: 'neutral' };
  // Расторгнутый договор в карточке показывает ФАКТ ЗАКЛЮЧЕНИЯ (владелец, 2026-07-29):
  // красный бейдж «Расторгнут» уезжает на отдельную карточку расторжения ниже,
  // а здесь остаётся зелёный «Подписан» + серая пометка с датой расторжения.
  // В таблицах (AmbassadorRow, «Все договоры») статус по-прежнему «Расторгнут».
  const latestTermination = terminations[0] ?? null;
  const badgeMeta = contract.status === 'terminated'
    ? { label: 'Подписан', tone: CONTRACT_TONE.completed }
    : { label: statusMeta.label, tone: CONTRACT_TONE[contract.status] || statusMeta.tone };
  const canSign = contract.status === 'submitted';
  const canViewData = contract.status !== 'draft' && contract.status !== 'form_sent';

  return (
    <>
    <article className="oc-lead-card__docs-contract">
      <header className="oc-lead-card__docs-contract-head">
        <span className={`oc-lead-card__docs-status oc-lead-card__chip oc-lead-card__chip--${badgeMeta.tone}`}>
          {badgeMeta.label}
        </span>
        {(() => { const ab = appBadge(contract.application); return <span className={ab.className} title="Приложение договора">{ab.label}</span>; })()}
        {contract.isDirectStub && (
          <span className="oc-lead-card__chip oc-lead-card__chip--orange" title="Автосозданный черновик под прямые выплаты — форма и подписание недоступны">
            Заглушка для прямых выплат
          </span>
        )}
        {contract.postingViaVpn && (
          <span className="oc-lead-card__chip oc-lead-card__chip--blue oc-lead-card__docs-vpn"
                title="Постинг через VPN под США">
            <Icon name="shield-check" weight="fill" size="sm" />VPN US
          </span>
        )}
        <span className="oc-lead-card__docs-date">от {formatDate(contract.createdAt)}</span>
        {contract.status === 'terminated' && latestTermination && (
          <span className="oc-lead-card__docs-date">
            · расторгнут {formatDate(latestTermination.documentDate)}
          </span>
        )}
      </header>

      <div className="oc-lead-card__docs-contract-body">
        <dl className="oc-lead-card__docs-grid">
          <div className="oc-lead-card__docs-cell">
            <dt className="oc-lead-card__docs-cell-label">Тип</dt>
            <dd className="oc-lead-card__docs-cell-value">{contract.placementType === 'onetime' ? 'Разовое размещение' : 'Постоянное'}</dd>
          </div>
          <div className="oc-lead-card__docs-cell">
            <dt className="oc-lead-card__docs-cell-label">Действует с</dt>
            <dd className="oc-lead-card__docs-cell-value">{formatDate(contract.effectiveDate)}</dd>
          </div>
          {/* Phase 75 DOC-02 / D-31: «Подписан» — render only when signedAt is set
              (most contracts NULL forever; populated by Plan 75-07 webhook handler
              when Dropbox Sign reports signature_request_signed). */}
          {contract.signedAt && (
            <div className="oc-lead-card__docs-cell">
              <dt className="oc-lead-card__docs-cell-label">Подписан</dt>
              <dd className="oc-lead-card__docs-cell-value">{formatDate(contract.signedAt)}</dd>
            </div>
          )}
          <div className="oc-lead-card__docs-cell">
            <dt className="oc-lead-card__docs-cell-label">Сумма</dt>
            <dd className="oc-lead-card__docs-cell-value">{contract.ambassadorFee ? `${contract.ambassadorFee} $` : '—'}</dd>
          </div>
          {contract.placementType === 'onetime' ? (
            <div className="oc-lead-card__docs-cell">
              <dt className="oc-lead-card__docs-cell-label">Предоплата</dt>
              <dd className="oc-lead-card__docs-cell-value">{contract.hasPrepayment ? 'Да' : 'Нет'}</dd>
            </div>
          ) : contract.hasTrialPeriod && (
            <div className="oc-lead-card__docs-cell">
              <dt className="oc-lead-card__docs-cell-label">Тест</dt>
              <dd className="oc-lead-card__docs-cell-value">{contract.trialAmount ? `${contract.trialAmount} $` : '—'}</dd>
            </div>
          )}
          {contract.placementType === 'onetime' && contract.prepaymentAmount != null && (
            <div className="oc-lead-card__docs-cell">
              <dt className="oc-lead-card__docs-cell-label">Сумма предоплаты</dt>
              <dd className="oc-lead-card__docs-cell-value">{contract.prepaymentAmount} $</dd>
            </div>
          )}
        </dl>
        {contract.signatureRequestId && (
          <div className="contract-card__sig-id" title="ID документа в Dropbox Sign (используется в инвойсе как Agreement No.)">
            № {contract.signatureRequestId}
          </div>
        )}
        <div className="contract-card__contract-id" title="Внутренний ID контракта (для сопоставления с выгрузкой)">
          Контракт: {String(contract.id).toUpperCase()}
        </div>

      <div className="oc-lead-card__docs-actions-row">
        {!isSignedContract(contract.status) ? (
          <>
            {/* Form stage — visible only until the contract is signed */}
            {!contract.isDirectStub && (
              <>
                <button
                  type="button"
                  className="documents-action-btn"
                  onClick={handleCopyFormLink}
                  disabled={!contract.formToken}
                  title="Скопировать ссылку на форму"
                >
                  Копировать ссылку
                </button>
                <button
                  type="button"
                  className="documents-action-btn"
                  disabled={contract.status !== 'draft' || linkSentMutation.isPending}
                  onClick={() => linkSentMutation.mutate()}
                  title="Отметить что ссылка отправлена амбассадору вручную"
                >
                  Ссылка отправлена
                </button>
              </>
            )}
            <button
              type="button"
              className="documents-action-btn"
              disabled={!canViewData}
              onClick={() => setViewDataOpen(true)}
              title="Данные амбассадора"
            >
              Данные
            </button>
            {!contract.isDirectStub && (
              <button
                type="button"
                className="documents-action-btn"
                disabled={!canSign || sendMutation.isPending}
                onClick={() => sendMutation.mutate(contract.id)}
              >
                На подпись
              </button>
            )}
            <button
              type="button"
              className="documents-action-btn documents-action-btn--danger"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              Удалить
            </button>
          </>
        ) : (
          <>
            {/* Signed (completed OR terminated) — data + the universal icon row
                (final PDF / Dropbox / copy link). Расторжение не прячет договор:
                подписанный PDF нужен для налоговой/юр. истории. */}
            <button
              type="button"
              className="documents-action-btn"
              disabled={!canViewData}
              onClick={() => setViewDataOpen(true)}
              title="Данные амбассадора"
            >
              Данные
            </button>
            <DocumentActions
              onDownload={handleDownloadSignedPdf}
              downloadTitle="Финальный .pdf"
              entity="contract"
              entityId={contract.id}
              dropboxUrl={contract.dropboxUrl ?? null}
              sharedUrl={contract.sharedUrl ?? null}
            />
          </>
        )}
        {!isAccountant && (
          <button type="button" className="documents-action-btn"
                  disabled={vpnMutation.isPending}
                  title="Постинг через VPN под США"
                  onClick={() => vpnMutation.mutate(!contract.postingViaVpn)}>
            {contract.postingViaVpn ? 'Выключить' : 'Включить'} VPN (США)
          </button>
        )}
      </div>

      <section className="oc-lead-card__docs-section">
        <div className="oc-lead-card__docs-section-head">
          <h4 className="oc-lead-card__docs-title oc-lead-card__docs-section-title">Инвойсы{sortedInvoices.length > 0 && <span className="oc-lead-card__docs-count"> · {sortedInvoices.length}</span>}</h4>
          <button
            type="button"
            className="documents-action-btn documents-action-btn--primary"
            disabled={contract.status !== 'completed'}
            title={contract.status !== 'completed' ? 'Договор должен быть подписан' : undefined}
            onClick={() => { setEditingDraftInvoice(null); setInvoiceModalOpen(true); }}
          >
            + Инвойс
          </button>
        </div>

        {/* INVOICE-FORM-UX: the section's one form slot — create XOR edit, in flow. The editor is keyed by its
            invoice and closes the slot only while the slot still holds that invoice: a late save of draft A
            must not close the open editor of draft B. */}
        {invoiceModalOpen && (
          <CreateInvoiceModal
            contract={contract}
            onClose={() => setInvoiceModalOpen(false)}
            host="inline"
          />
        )}
        {editingDraftInvoice && (
          <EditDraftInvoiceModal
            key={editingDraftInvoice.id}
            invoice={editingDraftInvoice}
            leadId={contract.leadId}
            contractStatus={contract.status}
            onClose={() => setEditingDraftInvoice((cur) => (cur?.id === editingDraftInvoice.id ? null : cur))}
            onSaved={() => {
              qc.invalidateQueries({ queryKey: queryKeys.invoices.byContract(contract.id) });
              qc.invalidateQueries({ queryKey: queryKeys.invoices.adminList() });
            }}
          />
        )}

        {invoicesLoading ? (
          <p className="oc-lead-card__docs-empty">Загрузка…</p>
        ) : sortedInvoices.length === 0 ? (
          <p className="oc-lead-card__docs-empty">Инвойсов пока нет</p>
        ) : (
          /*
            Phase 65 polish (post-UAT): replace the 7-column table with a
            compact 2-row card layout. The table version overflowed the
            narrow LeadSidePanel container; cards wrap naturally and stay
            readable down to ~280px wide.
          */
          <div className="oc-lead-card__docs-list">
            {sortedInvoices.map((inv) => {
              // Phase 65 D-04: per-invoice payment method+address (D-25
              // stores these on every invoice row; pass as synthetic
              // contract+ambassador shapes to reuse the shared formatter).
              const pm = formatPaymentMethod(
                { paymentMethod: inv.paymentMethod },
                { paymentAddress: inv.paymentAddress },
              );
              const fullNo = String(inv.invoiceNo || inv.id);
              // INVOICE-APP: the pack's application; a packless invoice (legacy / onetime donor) gets none.
              const appChip = inv.pack ? appBadge(inv.pack.application) : null;
              return (
                <div key={inv.id} data-invoice-id={inv.id} className={`oc-lead-card__docs-invoice${focusId === inv.id ? ' oc-lead-card__docs-invoice--focus' : ''}`}>
                  {/* Row 1: pill + amount + actions */}
                  <div className="oc-lead-card__docs-invoice-top">
                    {/* Phase 75.1: interactive InvoiceStatusDropdown (was read-only in 75-12).
                        MANAGER role keeps the {draft, in_payment, void} subset; a paid invoice's
                        dropdown is disabled. Backend assertTransition is the authoritative gate. */}
                    <InvoiceStatusDropdown
                      appearance='chip'
                      currentStatus={inv.status || 'draft'}
                      role={user?.role}
                      disabled={false}
                      onChange={async (nextStatus) => {
                        try {
                          await updateInvoiceStatus(inv.id, nextStatus);
                          qc.invalidateQueries({ queryKey: queryKeys.invoices.byContract(contract.id) });
                          qc.invalidateQueries({ queryKey: queryKeys.invoices.adminList() });
                          qc.invalidateQueries({ queryKey: queryKeys.invoices.log(inv.id) });
                          addToast('Готово', 'Статус инвойса обновлён', 'success');
                        } catch (err) {
                          const msg = err?.response?.data?.error || 'Не удалось обновить статус инвойса';
                          addToast('Ошибка', msg, 'error');
                        }
                      }}
                    />
                    <span className="oc-lead-card__docs-amount">
                      USD {Number(inv.fee).toFixed(2)}
                    </span>
                    {/* Invoice kind chip — FROZEN snapshot stored at creation in
                        invoice.kind (test|ambassador). NOT re-derived from the lead's
                        current status; a mis-derived kind is corrected via the draft
                        editor (PATCH /invoices/:id { kind }). */}
                    <span className={`oc-lead-card__chip oc-lead-card__chip--${inv.kind === 'test' ? 'orange' : 'blue'}`}>
                      {inv.kind === 'test' ? 'Тестовый' : 'Постоянный'}
                    </span>
                    {/* P3: paymentBasis chip — manual (По амбассадору | Разовое размещение), next to kind chip. */}
                    <span className={`oc-lead-card__chip oc-lead-card__chip--${BASIS_TONE[inv.paymentBasis || 'ambassador'] || 'green'}`}>
                      {paymentBasisLabel(inv.paymentBasis || 'ambassador')}
                    </span>
                    {appChip && <span className={appChip.className}>{appChip.label}</span>}
                    <span style={{ flex: 1 }} />
                    {/* Universal row: download invoice / open in Dropbox / copy share link */}
                    {inv.paymentBasis !== 'direct' && (
                      <DocumentActions
                        onDownload={() => downloadInvoicePdf(inv.id, `${inv.invoiceNo}.pdf`)}
                        downloadTitle="Скачать инвойс"
                        entity="invoice"
                        entityId={inv.id}
                        dropboxUrl={inv.dropboxUrl ?? null}
                        sharedUrl={inv.sharedUrl ?? null}
                        contractId={contract.id}
                      />
                    )}
                    <button
                      type="button"
                      className="oc-lead-card__docs-iconbtn"
                      onClick={() => setLogModalInvoiceId(inv.id)}
                      title="История изменений статуса"
                    >
                      <Icon name="clock-counter-clockwise" size="sm" />
                    </button>
                    {/* Phase 100: edit account + videoCount while invoice is draft. */}
                    {inv.status === 'draft' && inv.paymentBasis !== 'direct' && (
                      <button
                        type="button"
                        className="oc-lead-card__docs-iconbtn"
                        onClick={() => { setInvoiceModalOpen(false); setEditingDraftInvoice(inv); }}
                        title="Редактировать аккаунт и количество видео"
                        aria-label="Редактировать черновик"
                      >
                        <Icon name="pencil-simple" size="sm" />
                      </button>
                    )}
                    {/* Phase 75.1: permanent delete button restored (was removed in Phase 75
                        OQ-1). Visible only when status='void' AND user is ADMIN or ACCOUNTANT
                        — keeps per-contract numbering tidy (deleting most-recent void frees its
                        seqNo for the next created invoice via MAX(seqNo)+1). */}
                    {canDeleteVoid && inv.status === 'void' && (
                      <button
                        type="button"
                        className="oc-lead-card__docs-iconbtn oc-lead-card__docs-iconbtn--danger"
                        onClick={async () => {
                          if (!window.confirm(`Удалить инвойс ${inv.invoiceNo} навсегда? История статусов также будет удалена.`)) return;
                          try {
                            await deleteInvoice(inv.id);
                            qc.invalidateQueries({ queryKey: queryKeys.invoices.byContract(contract.id) });
                            qc.invalidateQueries({ queryKey: queryKeys.invoices.adminList() });
                            addToast('Готово', 'Инвойс удалён', 'success');
                          } catch (err) {
                            addToast('Ошибка', err?.response?.data?.error || 'Не удалось удалить инвойс', 'error');
                          }
                        }}
                        title="Удалить инвойс навсегда"
                      >
                        <Icon name="trash" size="sm" />
                      </button>
                    )}
                  </div>

                  {/* Row 2: full invoice number — own line, click-to-copy */}
                  <button
                    type="button"
                    className="oc-lead-card__docs-invoice-no"
                    onClick={() => copyToClipboard(fullNo)}
                    title="Нажмите чтобы скопировать"
                  >
                    № {fullNo}
                  </button>

                  {/* Phase 68 D-01 / D-04 / D-12: compact issue date + clickable period zone.
                      Click on the period text opens an inline MonthPicker; selecting a month
                      saves via PATCH /invoices/:id/period and invalidates byContract + adminList.
                      Backend gates by role + per-row scope (Plan 03). Phase 2 (2026-06-12):
                      ADMIN/TEAM_LEAD only — MANAGER sees a plain span (backend 403s otherwise). */}
                  <div className="oc-lead-card__docs-meta">
                    <span>{formatCompactDate(inv.issueDate)}</span>
                    <span>·</span>
                    {editingPeriodInvoiceId === inv.id ? (
                      <MonthPicker
                        value={(inv.periodStart || '').slice(0, 7)}
                        onChange={async (newMonth) => {
                          if (!newMonth) { setEditingPeriodInvoiceId(null); return; }
                          // Mirror CreateInvoiceModal's month → start/end derivation (Phase 65 D-15).
                          const [y, m] = newMonth.split('-').map(Number);
                          const periodStart = `${y}-${String(m).padStart(2, '0')}-01`;
                          const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
                          const periodEnd = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
                          try {
                            await updateInvoicePeriod(inv.id, { periodStart, periodEnd });
                            qc.invalidateQueries({ queryKey: queryKeys.invoices.byContract(contract.id) });
                            qc.invalidateQueries({ queryKey: queryKeys.invoices.adminList() });
                            addToast('Готово', 'Период инвойса обновлён', 'success');
                          } catch (err) {
                            // api/client.js interceptor разворачивает axios-ошибку в plain Error:
                            // текст бэкенда лежит в err.message/err.data, .response не существует.
                            const msg = err?.data?.error || err?.message || 'Не удалось обновить период';
                            addToast('Ошибка', msg, 'error');
                          }
                          setEditingPeriodInvoiceId(null);
                        }}
                      />
                    ) : !canEditPeriod || inv.status === 'paid' ? (
                      // No `__docs-period` here: that rule carries the editable affordance.
                      // `#475569` → --text-secondary (D3), which `__docs-meta` already sets.
                      <span>{formatMonthYear(inv.periodStart)}</span>
                    ) : (
                      <button
                        type="button"
                        className="oc-lead-card__docs-period"
                        onClick={() => setEditingPeriodInvoiceId(inv.id)}
                        title="Кликните чтобы изменить период"
                        aria-label="Изменить период инвойса"
                      >
                        {formatMonthYear(inv.periodStart)}
                      </button>
                    )}
                    {inv.status === 'paid' && (
                      <>
                        <span>·</span>
                        <span>Оплаченный инвойс не редактируется</span>
                      </>
                    )}
                  </div>

                  {/* Row 4: payment method (only if known) */}
                  {pm.providerLabel && (
                    <div className="oc-lead-card__docs-meta">
                      <span className="oc-lead-card__docs-meta-value">{pm.providerLabel}</span>
                      {pm.addressDetail && (
                        <>
                          <span>·</span>
                          <span>{pm.addressDetail}</span>
                        </>
                      )}
                    </div>
                  )}

                  {/* Phase 101 W6 (2026-05-25 cleanup): single info line
                      «Пачка: название · Видео: N». Pack-pill row removed
                      per user feedback — was duplicating the handle and
                      adding noise. Rebind chip + cpmAtPaid badge stay on the
                      same row when relevant. */}
                  <div className="oc-lead-card__docs-meta">
                    <span>
                      <span className="oc-lead-card__docs-meta-key">{inv.pack ? 'Пачка:' : 'Аккаунты:'}</span>{' '}
                      {inv.pack
                        ? packDisplayName(inv.pack)
                        : (inv.account?.handle
                            ? `${inv.account.platform}: ${inv.account.handle}`
                            : '—')}
                    </span>
                    <span>
                      <span className="oc-lead-card__docs-meta-value">Видео:</span>{' '}
                      {inv.videoCount == null ? '—' : inv.videoCount}
                    </span>
                    {/* P3: manual bonusVideoCount (NOT NULL default 0). */}
                    <span>
                      <span className="oc-lead-card__docs-meta-value">Бонусы:</span>{' '}
                      {inv.bonusVideoCount == null ? '—' : inv.bonusVideoCount}
                    </span>
                    {/* P-E2 D7 — PM: «внутри инвойса не нужно расписывать за что деньги, можно просто
                        для нас в CRM фиксировать»: one hint item, never the breakdown. P-E1 D4 makes the
                        base derived, so this sum equals fee − baseFee by construction — summed from
                        extraPayments in cents so a row whose baseFee never arrived renders nothing. */}
                    {Boolean(inv.extraPayments?.length) && (
                      <span>
                        <span className="oc-lead-card__docs-meta-value">Допвыплаты:</span>{' '}
                        USD {(inv.extraPayments.reduce((s, e) => s + Math.round(Number(e.amount) * 100), 0) / 100).toFixed(2)}
                      </span>
                    )}
                    {inv.cpmAtPaid != null && (
                      <span className="cpm-frozen-badge" title="CPM на момент оплаты">
                        CPM инвойса: {Number(inv.cpmAtPaid).toFixed(2)}
                      </span>
                    )}
                    {/* ADMIN может (пере)привязать пачку у любого неоплаченного инвойса;
                        остальным — только legacy без пачки. Оплаченный не редактируется. */}
                    {inv.status !== 'paid' && ((!inv.pack && inv.accountId) || isAdmin) && contract.placementType !== 'onetime' && inv.paymentBasis !== 'direct' && (
                      <button
                        type="button"
                        className="oc-lead-card__chip oc-lead-card__chip--orange oc-lead-card__chip--button oc-lead-card__docs-packchip"
                        onClick={() => setRebindFor(inv)}
                        title={inv.pack ? 'Сменить пачку инвойса' : 'Нет пачки — нажмите чтобы привязать'}
                      >
                        <Icon name="pencil-simple" size="sm" />
                        {inv.pack ? 'сменить пачку' : 'привязать пачку'}
                      </button>
                    )}
                    {rebindFor?.id === inv.id && inv.paymentBasis !== 'direct' && (
                      <PackRebindPopover
                        invoice={rebindFor}
                        contractId={contract.id}
                        leadId={lead?.id ?? contract.leadId}
                        onClose={() => setRebindFor(null)}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Секция живёт только у ЗАВЕРШЁННОГО договора — это точка входа «оформить
          расторжение» + «Расторгнуть». Сами расторжения рендерятся отдельными
          карточками ниже (владелец, 2026-07-29). У terminated тут делать нечего:
          POST /terminations требует status='completed' и всё равно вернул бы 409. */}
      {contract.status === 'completed' && (
        <section className="oc-lead-card__docs-section">
          <div className="oc-lead-card__docs-section-head">
            <h4 className="oc-lead-card__docs-title oc-lead-card__docs-section-title">Расторжение{terminations.length > 0 && <span className="oc-lead-card__docs-count"> · {terminations.length}</span>}</h4>
            <button
              type="button"
              className="documents-action-btn documents-action-btn--primary"
              onClick={() => setTerminationModalOpen(true)}
            >
              + Расторжение
            </button>
          </div>

          {terminationModalOpen && (
            <CreateTerminationModal
              contractId={contract.id}
              onClose={() => setTerminationModalOpen(false)}
            />
          )}

          {terminations.length === 0 ? (
            <p className="oc-lead-card__docs-empty">Расторжений пока нет</p>
          ) : (
            <p className="oc-lead-card__docs-empty">
              Оформлено расторжений: {terminations.length} — отдельной карточкой ниже
            </p>
          )}

          {contract.status === 'completed' && (
            <button
              type="button"
              className="documents-action-btn documents-action-btn--danger"
              style={{ marginTop: 10 }}
              disabled={terminateMutation.isPending}
              onClick={() => {
                if (window.confirm('Расторгнуть договор? Статус сменится на «Расторгнут».')) {
                  terminateMutation.mutate();
                }
              }}
            >
              Расторгнуть
            </button>
          )}
        </section>
      )}
      </div>

      {viewDataOpen && (
        <AmbassadorDataModal
          contractId={contract.id}
          onClose={() => setViewDataOpen(false)}
        />
      )}
      {logModalInvoiceId && (
        <InvoiceStatusLogModal
          invoiceId={logModalInvoiceId}
          onClose={() => setLogModalInvoiceId(null)}
        />
      )}
    </article>

    {/* Расторжение — САМОСТОЯТЕЛЬНАЯ карточка того же уровня, сразу под договором
        (владелец, 2026-07-29): менеджеру нужно видеть оба документа как равные —
        и заключение, и расторжение, каждый со своей кнопкой скачивания. */}
    {terminations.map((t) => (
      <article key={t.id} className="oc-lead-card__docs-contract">
        <header className="oc-lead-card__docs-contract-head">
          <span className="oc-lead-card__chip oc-lead-card__chip--red">
            {CONTRACT_STATUS.terminated.label}
          </span>
          <span className="oc-lead-card__docs-date">от {formatDate(t.documentDate)}</span>
        </header>

        <div className="oc-lead-card__docs-contract-body">
          <dl className="oc-lead-card__docs-grid">
            <div className="oc-lead-card__docs-cell">
              <dt className="oc-lead-card__docs-cell-label">Инициатор</dt>
              <dd className="oc-lead-card__docs-cell-value">{t.initiator === 'blogger' ? 'Блогер' : 'Компания'}</dd>
            </div>
            <div className="oc-lead-card__docs-cell">
              <dt className="oc-lead-card__docs-cell-label">К договору от</dt>
              <dd className="oc-lead-card__docs-cell-value">{formatDate(contract.effectiveDate)}</dd>
            </div>
            {t.initiator === 'blogger' && (
              <div className="oc-lead-card__docs-cell">
                <dt className="oc-lead-card__docs-cell-label">Подпись</dt>
                <dd className="oc-lead-card__docs-cell-value">
                  {t.signedAt
                    ? `Подписано ${formatDate(String(t.signedAt).slice(0, 10))}`
                    : t.signatureRequestId ? 'Ожидает подписи' : 'Не отправлено'}
                </dd>
              </div>
            )}
          </dl>
          <div className="contract-card__contract-id" title="Внутренний ID расторжения">
            Расторжение: {String(t.id).toUpperCase()}
          </div>

        <div className="oc-lead-card__docs-actions-row">
          {t.initiator === 'blogger' && !t.signedAt && !t.signatureRequestId && (
            <button
              type="button"
              className="documents-action-btn"
              disabled={sendTerminationMutation.isPending}
              onClick={() => sendTerminationMutation.mutate(t.id)}
            >
              На подпись
            </button>
          )}
          {(isAdmin || isTeamLead) && !t.signedAt && !t.signatureRequestId && (
            <button
              type="button"
              className="documents-action-btn documents-action-btn--danger"
              onClick={async () => {
                if (!confirm('Удалить расторжение?')) return;
                await deleteTermination(t.id);
                qc.invalidateQueries({ queryKey: ['terminations', contract.id] });
              }}
            >
              Удалить
            </button>
          )}
          <DocumentActions
            onDownload={() => downloadTerminationPdf(t.id, `termination-${t.id}.pdf`)}
            downloadTitle="Скачать расторжение"
            entity="termination"
            entityId={t.id}
            contractId={contract.id}
            dropboxUrl={t.dropboxUrl ?? null}
            sharedUrl={t.sharedUrl ?? null}
          />
        </div>
        </div>
      </article>
    ))}
    </>
  );
}
